import { Button } from "V2.0/common/button";
import InputComponent from "V2.0/components/InputComponent";
import { Switch } from "V2.0/common/switch";
import { ExclamationCircleIcon } from "@heroicons/react/24/outline";

enum InterruptionMode {
  VAD,
  END_OF_PHRASE_DETECTION,
}

function DefaultInterruptionInfo() {
  return (
    <div className="flex gap-x-2 items-center w-full text-wrap">
      <ExclamationCircleIcon className="text-blue-500 size-4" />
      <p className="font-xs text-xs text-blue-500">
        If turned off, the assistant will be interrupted if the user speaks more
        than 2 words
      </p>
    </div>
  );
}

function Interruption(props) {
  const { interruption, onUpdate, onToggle, locked } = props;

  function handleUpdateSpeechThreshold(speechThreshold) {
    onUpdate({
      speechThreshold,
    });
  }

  function handleUpdateUpdateMinSpeechDuration(minSpeechDuration) {
    onUpdate({ minSpeechDuration });
  }

  function handleUpdateMinSilenceDuration(minSilenceDuration) {
    onUpdate({ minSilenceDuration });
  }

  return (
    <div className="grid grid-cols-3 space-y-4 px-6">
      <div className="col-span-3 flex flex-col gap-y-2">
        <div className="flex items-center gap-x-2">
          <p className="text-base font-semibold">
            Use Speech Activity Detection
          </p>
          <Switch
            color="emerald"
            checked={interruption.vadInterruptionEnabled}
            onClick={() => onToggle(InterruptionMode.VAD)}
            disabled={locked}
          />
        </div>
        <DefaultInterruptionInfo />
      </div>

      <div className="sm:col-span-3 lg:col-span-2 xl:col-span-2 flex flex-col ml-4">
        <InputComponent
          type="slider"
          min={0}
          max={1}
          step={0.1}
          disabled={!interruption.vadInterruptionEnabled || locked}
          value={interruption.speechThreshold}
          label="Voice Sensitivity"
          description="How loud a user must speak to interrupt the agent"
          onChange={(e) => handleUpdateSpeechThreshold(e.currentTarget.value)}
        />
        <InputComponent
          type="slider"
          min={0.2}
          max={0.6}
          step={0.05}
          disabled={!interruption.vadInterruptionEnabled || locked}
          value={interruption.minSpeechDuration}
          label="Speech Duration"
          description="Minimum duration of Speech to pause the assistant"
          onChange={(e) =>
            handleUpdateUpdateMinSpeechDuration(e.currentTarget.value)
          }
        />

        <InputComponent
          type="slider"
          min={0.2}
          max={1.5}
          step={0.05}
          disabled={!interruption.vadInterruptionEnabled || locked}
          value={interruption.minSilenceDuration}
          label="Silence Duration"
          description="Minimum duration of Silence to resume the assistant"
          onChange={(e) =>
            handleUpdateMinSilenceDuration(e.currentTarget.value)
          }
        />
      </div>
    </div>
  );
}

export default function SpeechActivityDetection(props) {
  const {
    onUpdateInterruption,
    onToggleBetweenVadInterruptionAndEndOfPhraseDetection,
    settings,
    onSave,
    locked
  } = props;

  return (
    <div className="flex flex-col gap-y-4 border rounded-lg min-w-[600px]">
      <div className="px-6 py-4 bg-gray-50 rounded-t-lg">
        <p className="text-base font-semibold text-gray-900">
          Speech Activity Detection
        </p>
        <p className="text-sm mt-1 text-gray-500">
          Pauses the assistant's response, as soon as the user starts speaking
        </p>
      </div>

      <Interruption
        interruption={settings.interruption}
        onUpdate={onUpdateInterruption}
        onToggle={onToggleBetweenVadInterruptionAndEndOfPhraseDetection}
        locked={locked}
      />

      <div className="bg-gray-50 rounded-b-lg px-6 py-3 text-right">
        {!locked && <Button onClick={onSave}>Save</Button>}
      </div>
    </div>
  );
}
